import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  useWindowSize,
  affixPopoverToTrigger,
  calcPopoverPlacement,
  updatePopoverPlacement,
  handleClick
} from './helpers';

import defaultStyles from './styles.css';

const Popover = ({
  id,
  title,
  children,
  triggerId,
  isOpen,
  userStyles,
  clickHandler,
  windowSizeHandler
}) => {
  const popoverWrapperRef = React.createRef();
  const arrowContainerRef = React.createRef();
  const arrowRef = React.createRef();

  const [showPopover, setShowPopover] = useState(isOpen);

  const toggleShowPopover = () => {
    if (showPopover) {
      setShowPopover(false);
    } else {
      setShowPopover(true);
    }
  };

  const mountClickHandler = event => {
    event.preventDefault();

    const actions = {
      toggler: toggleShowPopover,
      setter: setShowPopover
    };
    const context = {
      popover: id,
      trigger: triggerId
    };

    if (event) {
      const { target } = event;
      clickHandler(target, actions, context);
    }
    return null;
  };

  const windowSize = windowSizeHandler();

  useEffect(() => {
    const currentReferences = {
      trigger: document.getElementById(triggerId),
      popover: popoverWrapperRef.current,
      arrowContainer: arrowContainerRef.current,
      arrow: arrowRef.current
    };
    const currentRefLocations = {
      trigger: currentReferences.trigger?.getBoundingClientRect(),
      popover: currentReferences.popover.getBoundingClientRect(),
      window: windowSize
    };
    window.addEventListener('click', mountClickHandler);

    const popoverInitialPlacement = calcPopoverPlacement(currentRefLocations);
    affixPopoverToTrigger(currentReferences.popover, popoverInitialPlacement);

    updatePopoverPlacement(currentReferences, currentRefLocations);
    return () => {
      window.removeEventListener('click', mountClickHandler);
    };
  });

  return (
    <div
      id={id}
      ref={popoverWrapperRef}
      aria-label="popover"
      className={`trp-popover-wrapper top middle ${
        showPopover ? 'visible' : 'hidden'
      }`}
      data-test="component-popover">
      <div className="trp-popover-surface">
        <button
          id={`${id}-close-button`}
          type="button"
          aria-label="close popover"
          data-test="close-button"
          className="trp-popover-close-button"
        />
        <div className="trp-popover-zones-wrapper">
          <div className="trp-popover-title-wrapper">
            <h2 data-test="popover-title">{title}</h2>
          </div>
          <div className="trp-popover-content-wrapper">
            <span data-test="popover-content">{children}</span>
          </div>
        </div>
      </div>
      <div
        ref={arrowContainerRef}
        className="trp-popover-arrow-wrapper trp-popover-arrow-middle">
        <div
          ref={arrowRef}
          className="trp-popover-arrow-bottom"
          data-test="popover-arrow"
        />
      </div>
      <style jsx>{defaultStyles}</style>
      <style jsx>{userStyles}</style>
    </div>
  );
};

Popover.defaultProps = {
  isOpen: false,
  userStyles: defaultStyles,
  clickHandler: handleClick,
  windowSizeHandler: useWindowSize
};

Popover.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  triggerId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  userStyles: PropTypes.string,
  clickHandler: PropTypes.func,
  windowSizeHandler: PropTypes.func
};

export default Popover;
